import React from 'react';
import { Link } from 'react-router-dom';
import ExternalLink from '../common/ExternalLink';

const OriginalProgressStats = ({ learningStats, appStats, techStats }) => {
  return (
    <div className="bg-white p-6 rounded-lg shadow-md overflow-hidden h-full flex flex-col">
      <h2 className="text-2xl font-semibold mb-4">Your Progress Stats</h2>
      <div className="flex-1 overflow-y-auto no-scrollbar">
        <div className="space-y-6">
          {/* Lesson Tree Progress */}
          {learningStats && (
            <div className="p-4 rounded-lg border border-gray-200 hover:border-blue-300 hover:shadow-md transition-all duration-300">
              <div className="flex items-center mb-2">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mr-3">
                  <svg
                    className="w-5 h-5 text-green-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                    />
                  </svg>
                </div>
                <h3 className="font-medium text-gray-800">Lesson Tree Progress</h3>
              </div>
              <div className="pl-13">
                <div className="mb-2">
                  <div className="flex justify-between mb-1">
                    <span className="text-sm font-medium text-gray-700">Core Concepts</span>
                    <span className="text-sm font-medium text-gray-700">
                      {learningStats.completedCoreNodes.length}/{learningStats.coreNodes.length}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-green-500 h-2 rounded-full"
                      style={{ width: `${learningStats.corePercentComplete}%` }}
                    ></div>
                  </div>
                </div>
                <div className="mb-2">
                  <div className="flex justify-between mb-1">
                    <span className="text-sm font-medium text-gray-700">Advanced Topics</span>
                    <span className="text-sm font-medium text-gray-700">
                      {learningStats.completedAdvancedNodes.length}/
                      {learningStats.advancedNodes.length}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-green-500 h-2 rounded-full"
                      style={{ width: `${learningStats.advancedPercentComplete}%` }}
                    ></div>
                  </div>
                </div>

                {learningStats.nextNodes.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <p className="text-sm font-medium text-gray-700 mb-2">Suggested next lesson:</p>
                    <div className="space-y-1">
                      {learningStats.nextNodes.slice(0, 1).map((node) => (
                        <Link
                          key={node.id}
                          to={node.path}
                          className="flex items-center text-sm text-blue-600 hover:text-blue-800 transition-colors"
                        >
                          <svg
                            className="w-3 h-3 mr-1"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 5l7 7-7 7"
                            />
                          </svg>
                          {node.title}
                        </Link>
                      ))}
                      {learningStats.nextNodes.length > 3 && (
                        <p className="text-xs text-gray-500 mt-1">
                          +{learningStats.nextNodes.length - 3} more unlocked lessons available
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Application Progress */}
          {appStats && (
            <div className="p-4 rounded-lg border border-gray-200 hover:border-blue-300 hover:shadow-md transition-all duration-300">
              <div className="flex items-center mb-2">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                  <svg
                    className="w-5 h-5 text-blue-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                    />
                  </svg>
                </div>
                <h3 className="font-medium text-gray-800">Application Progress</h3>
              </div>
              <div className="pl-13">
                <div className="grid grid-cols-3 gap-2 mb-2">
                  <div className="text-center p-2 bg-blue-50 rounded-lg">
                    <div className="text-xl font-bold text-blue-600">{appStats.total}</div>
                    <div className="text-xs text-gray-600">Applications Sent</div>
                  </div>
                  <div className="text-center p-2 bg-blue-50 rounded-lg">
                    <div className="text-xl font-bold text-blue-600">{appStats.interviews}</div>
                    <div className="text-xs text-gray-600">Interviewing</div>
                  </div>
                  <div className="text-center p-2 bg-blue-50 rounded-lg">
                    <div className="text-xl font-bold text-blue-600">{appStats.offers}</div>
                    <div className="text-xs text-gray-600">Offers</div>
                  </div>
                </div>
                <div className="text-sm text-gray-500">
                  <span className="font-medium text-blue-600">{appStats.thisWeek}</span> submitted
                  or updated this week
                </div>
              </div>
            </div>
          )}
          {/* Technical Interviews */}
          {techStats && (
            <div className="p-4 rounded-lg border border-gray-200 hover:border-blue-300 hover:shadow-md transition-all duration-300">
              <div className="flex items-center mb-2">
                <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                  <svg
                    className="w-5 h-5 text-purple-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <h3 className="font-medium text-gray-800">Technical Interviews</h3>
              </div>
              <div className="pl-13">
                <div className="flex items-center mb-2">
                  <div className="w-full bg-gray-200 rounded-full h-2 mr-2">
                    <div
                      className="bg-purple-500 h-2 rounded-full"
                      style={{ width: `${techStats.percentComplete}%` }}
                    ></div>
                  </div>
                  <span className="text-sm font-medium text-gray-700">
                    {techStats.solved}/{techStats.total}
                  </span>
                </div>

                {/* Difficulty-based progress */}
                <div className="grid grid-cols-3 gap-2 mb-2">
                  <div className="flex flex-col">
                    <div className="flex justify-between text-xs">
                      <span className="text-green-700 font-medium">Easy</span>
                      <span className="text-gray-600">
                        {techStats.easy.solved}/{techStats.easy.total}
                      </span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-1.5 mt-1">
                      <div
                        className="bg-green-500 h-1.5 rounded-full"
                        style={{ width: `${techStats.easy.percent}%` }}
                      ></div>
                    </div>
                  </div>
                  <div className="flex flex-col">
                    <div className="flex justify-between text-xs">
                      <span className="text-yellow-700 font-medium">Medium</span>
                      <span className="text-gray-600">
                        {techStats.medium.solved}/{techStats.medium.total}
                      </span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-1.5 mt-1">
                      <div
                        className="bg-yellow-500 h-1.5 rounded-full"
                        style={{ width: `${techStats.medium.percent}%` }}
                      ></div>
                    </div>
                  </div>
                  <div className="flex flex-col">
                    <div className="flex justify-between text-xs">
                      <span className="text-red-700 font-medium">Hard</span>
                      <span className="text-gray-600">
                        {techStats.hard.solved}/{techStats.hard.total}
                      </span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-1.5 mt-1">
                      <div
                        className="bg-red-500 h-1.5 rounded-full"
                        style={{ width: `${techStats.hard.percent}%` }}
                      ></div>
                    </div>
                  </div>
                </div>

                {/* Topic stats */}
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <div className="text-sm text-gray-600"></div>
                </div>

                {/* Recommendations */}
                <div className="mt-2 border-t border-gray-100 pt-2">
                  <div className="text-sm font-medium text-gray-700 mb-1">
                    Suggested next problem:
                  </div>
                  <div className="text-xs">
                    {techStats.nextProblem ? (
                      <ExternalLink
                        href={techStats.nextProblem.link || '/technical/leetcode'}
                        className="flex items-center text-blue-600 hover:text-blue-800 mb-1"
                      >
                        <span
                          className={`inline-block w-10 text-center rounded-sm px-1 mr-1
                          ${
                            techStats.nextProblem.difficulty?.toLowerCase() === 'easy'
                              ? 'bg-green-100 text-green-800'
                              : techStats.nextProblem.difficulty?.toLowerCase() === 'medium'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {techStats.nextProblem.difficulty?.toLowerCase() === 'medium'
                            ? 'Med'
                            : techStats.nextProblem.difficulty}
                        </span>
                        <span className="truncate">{techStats.nextProblem.question}</span>
                      </ExternalLink>
                    ) : (
                      <span className="text-gray-500">All problems completed! Amazing work.</span>
                    )}

                    {techStats.attempted > 0 && (
                      <div className="text-gray-500 mt-1">
                        <span className="font-medium text-purple-600">{techStats.attempted}</span>{' '}
                        problems currently in progress
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="mt-4 text-center">
        <Link
          to="/technical/leetcode"
          className="inline-flex items-center text-blue-500 hover:text-blue-700 transition-colors duration-300"
        ></Link>
      </div>
      <style jsx>{`
        .no-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .no-scrollbar::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
};

export default OriginalProgressStats;
